import React, { useState } from "react";
import axios from "axios";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveContactEndpoint() {
  if (typeof window === "undefined") return `${BACKEND_URL}/api/contact`;
  const host = window.location.hostname;
  const isPreview =
    host.includes("preview.emergentagent.com") ||
    host.includes("emergent.host") ||
    host === "localhost" ||
    host === "127.0.0.1";
  if (isPreview && BACKEND_URL) return `${BACKEND_URL}/api/contact`;
  return "/send_contact.php";
}

const ContactForm = ({ titleKey, variant = "hero", formType = "Contact" }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [status, setStatus] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const showStatus = (kind, msg) => {
    setStatus(kind);
    setStatusMsg(msg);
    setTimeout(() => {
      setStatus(null);
      setStatusMsg("");
    }, 6000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email) {
      showStatus("error", t("form.invalid"));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(resolveContactEndpoint(), {
        name: form.name,
        email: form.email,
        phone: form.phone,
        form_type: formType,
      });
      const msg = res?.data?.message || t("form.success");
      setForm({ name: "", email: "", phone: "" });
      showStatus("success", msg);
    } catch (err) {
      const detail = err?.response?.data?.detail || t("form.error");
      showStatus("error", detail);
    } finally {
      setLoading(false);
    }
  };

  const heroStyle = variant === "hero";
  const title = t(titleKey || "form.hero_title");

  return (
    <section className={heroStyle ? "relative bg-[#800000] py-16 md:py-20 overflow-hidden" : "bg-white dark:bg-black py-12 transition-colors"}>
      {heroStyle && (
        <>
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#14532d] rounded-full blur-3xl" />
          </div>
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#14532d]" />
          <div className="absolute right-0 top-0 bottom-0 w-1.5 bg-[#14532d]" />
        </>
      )}
      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className={`text-center font-bold mb-8 ${heroStyle ? "text-white text-4xl md:text-5xl" : "text-slate-900 dark:text-white text-3xl md:text-4xl"}`}>
          {title}
        </h1>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder={t("form.name")}
            required
            disabled={loading}
            className={heroStyle ? "bg-white border-0 h-12 text-slate-900 placeholder:text-slate-500" : "h-12 bg-white dark:bg-zinc-900 dark:border-zinc-800 dark:text-white"}
          />
          <Input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder={t("form.email")}
            required
            disabled={loading}
            className={heroStyle ? "bg-white border-0 h-12 text-slate-900 placeholder:text-slate-500" : "h-12 bg-white dark:bg-zinc-900 dark:border-zinc-800 dark:text-white"}
          />
          <Input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder={t("form.phone")}
            disabled={loading}
            className={heroStyle ? "bg-white border-0 h-12 text-slate-900 placeholder:text-slate-500" : "h-12 bg-white dark:bg-zinc-900 dark:border-zinc-800 dark:text-white"}
          />
          <div className="flex justify-center pt-2">
            <Button
              type="submit"
              disabled={loading}
              className={heroStyle ? "bg-white text-[#800000] hover:bg-emerald-50 px-10 py-6 text-base font-semibold rounded-md shadow-lg hover:shadow-xl transition-all disabled:opacity-70" : "bg-[#800000] hover:bg-[#5a0000] text-white px-10 py-6 text-base font-semibold rounded-md disabled:opacity-70"}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={18} className="animate-spin" />
                  {t("form.sending")}
                </span>
              ) : (
                t("form.submit")
              )}
            </Button>
          </div>

          {status === "success" && (
            <div className="flex items-start gap-2 p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-200 rounded-md text-sm">
              <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" />
              <span>{statusMsg}</span>
            </div>
          )}
          {status === "error" && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-200 rounded-md text-sm">
              <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
              <span>{statusMsg}</span>
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
